
import { camelCase } from 'change-case/keys';
const usePageStore = defineStore('page', {
    state: ()=>({ page: {}, loading: true, cancelLoading: true, isInitialized: false, cacheKeys:{}, breadcrumbsSchemaOrg: null, headerSchemaOrg: null, footerSchemaOrg: null, mainMenuSchemaOrg: null }), 
    actions:{
        isLoading(){
            return this.loading && !this.cancelLoading;
        },
        stopLoading(){
            setTimeout(() => { this.loading = false; }, 500);
        },
        initialize(pageDataRaw, key){
            this.loadPage(pageDataRaw, key)
        } ,
        loadPage(pageDataRaw, key){
            this.$reset()
            if(!pageDataRaw) throw new Error('usePageStore.initialize -> pageDataRaw is undefined');

            const pageData = camelCase(pageDataRaw);

            this.page = { ...pageData };

            const { fieldAttachments } = pageData;
            const   hasHeroImage       = Array.isArray(fieldAttachments)? !!fieldAttachments?.find(({ type })=> type === 'media--hero') : false ;

            this.page.hasHeroImage     = hasHeroImage;
            
            // Set isInitialized AFTER page data is fully loaded
            // Use key if provided, otherwise true
            this.isInitialized = key || true;
        } ,
        setBreadcrumbsSchemaOrg(schema){
            // Direct assignment to avoid triggering extra reactivity from $patch
            this.breadcrumbsSchemaOrg = schema;
        },
        setHeaderSchemaOrg(schema){
            this.headerSchemaOrg = schema;
        },
        setFooterSchemaOrg(schema){
            this.footerSchemaOrg = schema;
        },
        setMainMenuSchemaOrg(schema){
            this.mainMenuSchemaOrg = schema;
        },
        mapImage({  name,fieldMediaImage, thumbnail, drupalInternalMid, path, filename, fieldCaption, title, created, changed, fieldPublished, fieldWidth, fieldHeight, fieldMime, fieldSize, mediaImage }){
            const siteStore = useSiteStore();
            // Translated media entities often leave field_media_image empty even
            // though the field is non-translatable. For media--image, Drupal's
            // auto-populated `thumbnail` points at the same source file, so use
            // it as a fallback to keep images visible across all locales.
            const file      = fieldMediaImage?.uri?.url ? fieldMediaImage : thumbnail;
            const alt       = file?.meta?.alt || fieldMediaImage?.meta?.alt || name || filename || '';
            const src       = file?.uri?.url ? `${siteStore.host}${file.uri.url}` : undefined;

            return { name, url:path?.alias, alt, drupalInternalMid, src, fieldCaption, title, created, changed, fieldPublished, fieldWidth, fieldHeight, fieldMime, fieldSize, mediaImage}
        },
        mapDocumentImage({ uri, meta, created, changed, filename:name }){
            if(!uri || !name ) throw new Error('usePageStore.mapImage -> name or fieldMediaImage is undefined');

            const siteStore = useSiteStore();
            const alt       = meta?.alt|| name ;
            const src       = `${siteStore.host}${uri.url}`;

            return { name,  alt,  src, created, changed }
        }
    },
    getters:{
        isNcpsList(){ return this?.page?.type==="taxonomy_term--system_pages" && this.page.drupalInternalTid === 30 },
        isTopicsCommentsList(){ return this?.page?.type==="node--forum"},
        isTopicsList(){ return this?.page?.type==="taxonomy_term--forums"},
        isForumsList(){ return this?.page?.drupalInternalTid === 24 },
        isSystemPage(){ return this.page?.type === 'taxonomy_term--system_pages'; },
        isTaxonomyTermTag(){ return this.page?.type === 'taxonomy_term--tags'; },
        isChmNetwork(){ return systemPageTidConstants.CHM_NETWORK === this.page?.drupalInternalTid; },
        isSystemPageDev(){ return systemPageTidConstants.DEV === this.page?.drupalInternalTid; },
        isSearch(){
            const { SEARCH, SEARCH_SEC, SEARCH_BCH, SEARCH_ABS } = systemPageTidConstants;

            if (this.isContentType) return this?.page?.drupalInternalTid;

            if (!this.isSystemPage || this?.isNodePage || this?.isMediaPage)
                return false;

            if([ SEARCH, SEARCH_SEC,SEARCH_BCH, SEARCH_ABS ].includes(this.page?.drupalInternalTid))
                return  true;

            return false;
        },
        isSearchDrupalContentType(){
            const { SEARCH } = systemPageTidConstants;

            if (!this.isSearch) return false;

            if(SEARCH === this.page?.drupalInternalTid)
                return  SEARCH;

            return false;
        },
        isSearchSecretariat(){
            const { SEARCH_SEC } = systemPageTidConstants;

            if (!this.isSearch) return false;

            if(SEARCH_SEC === this.page?.drupalInternalTid)
                return SEARCH_SEC;

            return false;
        },
        isSearchBch(){
            const { SEARCH_BCH } = systemPageTidConstants;

            if (!this.isSearch) return false;

            if(SEARCH_BCH === this.page?.drupalInternalTid)
                return SEARCH_BCH;

            return false;
        },
        
        isSearchAbs(){
            const { SEARCH_ABS } = systemPageTidConstants;

            if (!this.isSearch) return false;

            if(SEARCH_ABS === this.page?.drupalInternalTid)
                return SEARCH_ABS;

            return false;
        },
        searchContentTypeIds(){
            return getTids(this?.page?.fieldSearch);
        },
        isPage(){
            if(this.isSystemPageDev || this.isSearch || this.isForumsList ||  this.isMediaPage || this.isForumsList || this.isNcpsList || this.isChmNetwork) return false;

            const pageTypes = ['node--content','taxonomy_term--system_pages', 'media--hero', 'media--image', 'media--document', 'media--remote_video', 'media--embed' ];
            if(pageTypes.includes(this?.page?.type)) return true;

            return false;
        },
        isMediaPage(){
            return ['media--hero', 'media--image', 'media--document', 'media--remote_video', 'media--embed'].includes(this?.page?.type);

        },
        mediaTypeName(){
            const name = this?.page?.type?.replace('media--', '').replaceAll('_', ' ') || '';

            // Title-case so the result matches the i18n keys ('Document', 'Image', 'Remote Video')
            return name.replace(/\b\w/g, (char)=> char.toUpperCase());
        },
        isTaxonomyPage(){
            return this?.page?.type?.startsWith('taxonomy_term--');
        },
        isNodePage(){
            return this?.page?.type?.startsWith('node--');
        },
        heroImage(){
            const heroImages = Array.isArray(this.page?.fieldAttachments)? this.page.fieldAttachments.filter(({ type })=> type === 'media--hero') : [];
        
            if(!heroImages.length) return undefined;
        
            // SSR-stable index: useState freezes the server-chosen value in the
            // Nuxt payload so the client hydrates with the same hero image.
            const picIndex = useSsrStableIndex(`hero-${heroImages.length}`, heroImages.length);

            return heroImages[picIndex.value];
        },

        typeName(){ 
            if(this.isTaxonomyTermTag || this.isSystemPage) return this.page?.name || '';

            if(this.isMediaPage) return  this.mediaTypeName
            const menusStore = useMenusStore();

            return  this.page?.fieldTypePlacement?.name || ''; 
        },
        typeNamePlural(){ 
            if(this.isSystemPage) return this.page?.name || '';
            if(this.isTaxonomyTermTag) return this.page?.fieldPlural || '';

            return  this.page?.fieldTypePlacement?.field_plural || ''; 
        },
        typeId(){ 
            
            return this.page?.fieldTypePlacement?.drupal_internal__tid || this.page?.fieldTypePlacement?.drupalInternalTid  || this.page?.drupalInternalTid || undefined; 
        },
        image(){
            if(this.isMediaImage) return this.page.fieldMediaImage;
            if(!this.images || !this.images?.length) return undefined;
        
            return {...this.images[0] };
        },
        document(){
            const siteStore   = useSiteStore();
            if(this.isMediaDocument) {
                const dl =  `${siteStore.host}${this.page?.fieldMediaDocument?.uri?.url}`;

                return {...this.page.fieldMediaDocument, downloadUrl:dl};
            }
            if( !this?.documents?.length) return undefined;
        
            
            const downloadUrl =  `${siteStore.host}${this.documents[0]?.fieldMediaDocument?.uri?.url}`;

            return { ...this.documents[0], downloadUrl };
        },

        video(){ 
            if(this.isMediaRemoteVideo) return this.page.fieldMediaRemoteVideo;
            return this?.videos?.length? this.videos[0] : undefined; },
        images(){
            if(!this.page?.fieldAttachments?.length ) return [];
        
            return this.page.fieldAttachments.filter(({ type })=> type === 'media--image').map( this.mapImage);
        },
        documents(){
            if(!this.page?.fieldAttachments?.length ) return [];
        
            return this.page.fieldAttachments.filter(({ type })=> type === 'media--document');
        },
        videos(){
          
            if(!this.page?.fieldAttachments?.length) return [];
        
            return this.page.fieldAttachments.filter(({ type })=> type === 'media--remote_video');
        },
        mediaImage(){
            const hasImage = this.page?.fieldMediaImage?.uri?.url;
        
            if(!hasImage) return undefined;

            const siteStore = useSiteStore();
        
            const alt = this.page.fieldMediaImage?.meta?.alt;
            const src = `${siteStore.host}${this.page.fieldMediaImage.uri.url}`;
        
            return { alt, src }
        },

        isImageOrVideo(){ return this.typeId === 16; },
        isImage(){ return this.isImageOrVideo && !this.videos.length; },
        isVideo(){ return this.isImageOrVideo && !!this.videos.length; },
        // BL-1417: only Image or video pages created on or before the cutoff auto-render their image/video
        showsImageOrVideoMedia(){ return this.isImageOrVideo && isCreatedOnOrBeforeAutoMediaCutoff(this.page?.created); },
        isDocument(){ return this.typeId === 12; },
        isMediaDocument(){ return this?.page?.type?.includes('document') },
        isMediaImage(){ return this?.page?.type?.includes('image') },
        isMediaRemoteVideo(){ return this?.page?.type?.includes('remote_video') },
        isMediaHero(){ return this?.page?.type?.includes('hero') },
        isMediaEmbed(){ return this?.page?.type === 'media--embed' },
        embedHtml(){ return this.isMediaEmbed ? embedFrameHtml(this.page?.fieldMediaInlineFrame, this.page?.name) : '' },
        isContentType(){
            // Content type pages are taxonomy_term--tags with a drupalInternalTid that matches a content type ID
            if (this?.page?.type !== 'taxonomy_term--tags') return false;
            const menusStore = useMenusStore();
            return menusStore.isContentTypeId(this?.page?.drupalInternalTid);
        },
        isSearchAll(){
            return this.isSearch && !this.isContentType && ( getTids(this?.page?.fieldSearch) || []).includes(27);
        },
        title(){
            return this.page?.title || this.page?.name;
        },
        // Drupal's page.front is a system path to a node or a term; match the page's own.
        isHomePage(){
            const { homePath }                             = useSiteStore();
            const { drupalInternalNid, drupalInternalTid } = this.page || {};

            if(!homePath) return false;

            return (!!drupalInternalNid && homePath === `/node/${drupalInternalNid}`)
                || (!!drupalInternalTid && homePath === `/taxonomy/term/${drupalInternalTid}`);
        },
        body(){
            return this.page?.body?.processed || this.page?.body?.value || this.page?.description?.processed || this.page?.description?.value;
        },
        hasChmEmbed(){
            const bodyHas        = this.page?.body?.value.includes('<script')        && this.page?.body?.value.includes('/widgets.js')        && this.page?.body?.value.includes('scbd-chm-embed');
            const descriptionHas = this.page?.description?.value.includes('<script') && this.page?.description?.value.includes('/widgets.js') && this.page?.description?.value.includes('scbd-chm-embed');

            return bodyHas || descriptionHas;
        },

        startDate(){
            return this.page?.fieldStartDate
        },
        endDate(){
            return this.page?.fieldEndDate
        },
        publishedOn(){
            return this.page?.fieldPublished || this.page?.created || this.page?.revisionCreated
        },
        url(){
            if(!this.page?.fieldUrl?.uri) return ''

            return this.page.fieldUrl.uri;
        },
        tags(){
            return this?.page?.tags || {}
        },
        media(){
            return this?.page?.fieldAttachments
        },
        migratedFromLink(){
            const { siteCode } = useSiteStore();

            return bioland1Url(siteCode, this.page?.fieldMigrated);
        },
    }
})


function getTids(searchField){
    if(!searchField || !searchField?.length) return false;

    return searchField.map(({ drupalInternalTid })=> drupalInternalTid);
}

// export { } form: keeps mlly/unimport from reading object keys as extra exports (BL-1178)
export { usePageStore };
